import { useState } from "react";

import { type AnyApiError, isSuccess } from "@/api/ApiResult";
import { useCrud } from "@/api/useCrud";
import { IconTrash } from "@/components/generated/icons";
import { Button } from "@/components/ui/Button/Button";
import { Modal } from "@/components/ui/Modal/Modal";
import { t } from "@/i18n/translate";
import type {
  DATA_ENTRY_RESET_REQUEST_PATH,
  DataEntrySource,
  DataEntryStatusName,
  PollingStationInvestigation,
} from "@/types/generated/openapi";
import { getDataEntrySourceNumber } from "@/utils/dataEntrySource";

interface ReadOnlyDataEntryDeleteProps {
  dataEntrySource: DataEntrySource;
  dataEntryId: number;
  status: DataEntryStatusName;
  onDeleted: () => void;
  onError: (error: AnyApiError) => void;
}

export function ReadOnlyDataEntryDelete({
  dataEntrySource,
  dataEntryId,
  status,
  onDeleted,
  onError,
}: ReadOnlyDataEntryDeleteProps) {
  const [showModal, setShowModal] = useState(false);
  const removePath: DATA_ENTRY_RESET_REQUEST_PATH = `/api/data_entries/${dataEntryId}`;
  const { remove, isLoading } = useCrud<PollingStationInvestigation>({ removePath });

  function toggleModal() {
    setShowModal(!showModal);
  }

  function handleDelete() {
    void remove().then((result) => {
      if (isSuccess(result)) {
        onDeleted();
      } else {
        onError(result);
        setShowModal(false);
      }
    });
  }

  const statusWithTwoEntries: DataEntryStatusName[] = [
    "second_entry_in_progress",
    "first_entry_correction",
    "second_entry_correction",
    "definitive",
  ];

  return (
    <div>
      <Button variant="tertiary" leftIcon={<IconTrash />} onClick={toggleModal}>
        {t("data_entry_detail.delete")}
      </Button>

      {showModal && (
        <Modal title={t("data_entry_detail.delete")} onClose={toggleModal}>
          {statusWithTwoEntries.includes(status) ? (
            <p>{t("data_entry_detail.delete_all_are_you_sure", { nr: getDataEntrySourceNumber(dataEntrySource) })}</p>
          ) : (
            <p>{t("data_entry_detail.delete_are_you_sure", { nr: getDataEntrySourceNumber(dataEntrySource) })}</p>
          )}
          <nav>
            <Button
              leftIcon={<IconTrash />}
              variant="primary-destructive"
              size="lg"
              onClick={handleDelete}
              disabled={isLoading}
            >
              {t("data_entry_detail.delete_data_entry")}
            </Button>
            <Button variant="secondary" size="xl" onClick={toggleModal} disabled={isLoading}>
              {t("cancel")}
            </Button>
          </nav>
        </Modal>
      )}
    </div>
  );
}
